import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Chip,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { RefreshCcw, Shield } from 'lucide-react';
import { useCodeAuditLogs } from '../../services/code-agent.service';
import { EmptyState } from '../../components/admin/EmptyState';
import { formatDateTime } from '../../utils/dateFormat';

export const CodeAgentAuditPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [stateFilter, setStateFilter] = useState<string>('');
  const [turnIdFilter, setTurnIdFilter] = useState<string>('');
  const [actionIdFilter, setActionIdFilter] = useState<string>('');
  const [recoveryIncidentIdFilter, setRecoveryIncidentIdFilter] = useState<string>('');
  
  const { data: response, isLoading, isFetching, error, refetch } = useCodeAuditLogs(page + 1, pageSize, {
    eventType: eventTypeFilter || undefined,
    source: sourceFilter || undefined,
    state: stateFilter || undefined,
    turnId: turnIdFilter.trim() || undefined,
    actionId: actionIdFilter.trim() || undefined,
    recoveryIncidentId: recoveryIncidentIdFilter.trim() || undefined,
  });

  const [selectedEventData, setSelectedEventData] = useState<Record<string, any> | null>(null);

  const logs = response?.data || [];
  const totalCount = response?.meta?.total || logs.length;

  const columns: GridColDef[] = [
    { field: 'publicId', headerName: 'ID', width: 130 },
    { 
      field: 'sessionId', 
      headerName: 'Session ID', 
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" className="text-gray-900 dark:text-slate-100">
          {params.value || 'N/A'}
        </Typography>
      )
    },
    { 
      field: 'workspace', 
      headerName: 'Workspace', 
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" className="truncate text-gray-900 dark:text-slate-100">
          {params.row.session?.conversation?.workspace?.fingerprint || 'Unknown'}
        </Typography>
      )
    },
    {
      field: 'eventType',
      headerName: 'Event Type',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" className="font-medium text-blue-600 dark:text-blue-400">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 105,
      renderCell: (params) => (
        <Chip size="small" label={params.value || 'audit'} variant="outlined" />
      ),
    },
    {
      field: 'state',
      headerName: 'State',
      width: 130,
      renderCell: (params) => (
        <Typography variant="body2" className="text-gray-900 dark:text-slate-100">
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDateTime(params.value, true)}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Event Data',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Button 
          size="small" 
          variant="outlined"
          onClick={() => setSelectedEventData(params.row.eventData)}
          disabled={!params.row.eventData}
          className="text-xs py-0.5 px-2"
        >
          View JSON
        </Button>
      ),
    },
  ];

  return (
    <Box className="min-h-screen bg-slate-50 p-6 dark:bg-slate-900">
      {/* Header */}
      <Box className="mb-6 flex justify-between items-center">
        <Box>
          <Typography variant="h4" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Code Agent Audit Logs
          </Typography>
          <Typography variant="body2" className="text-gray-600 dark:text-slate-400">
            Review security events, tool approvals, and critical agent actions.
          </Typography>
        </Box>
        <Box className="flex flex-wrap gap-3 items-center justify-end">
          <FormControl size="small" className="w-48 bg-white dark:bg-slate-800">
            <InputLabel>Event Type</InputLabel>
            <Select
              value={eventTypeFilter}
              label="Event Type"
              onChange={(e) => setEventTypeFilter(e.target.value)}
            >
              <MenuItem value="">All Events</MenuItem>
              <MenuItem value="turn.">Turns</MenuItem>
              <MenuItem value="action.">Actions</MenuItem>
              <MenuItem value="recovery.">Recovery</MenuItem>
              <MenuItem value="unknown">Unknown Actions</MenuItem>
              <MenuItem value="code_agent.kill_switches.updated">Kill Switch Updates</MenuItem>
              <MenuItem value="code_agent.session.killed">Single Session Kills</MenuItem>
              <MenuItem value="code_agent.sessions.revoked_all">Session Revocations</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" className="w-36 bg-white dark:bg-slate-800">
            <InputLabel>Source</InputLabel>
            <Select
              value={sourceFilter}
              label="Source"
              onChange={(e) => setSourceFilter(e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="audit">Audit</MenuItem>
              <MenuItem value="turn">Turn</MenuItem>
              <MenuItem value="action">Action</MenuItem>
              <MenuItem value="recovery">Recovery</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="State"
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="w-36 bg-white dark:bg-slate-800"
            placeholder="unknown"
          />
          <TextField
            size="small"
            label="Turn ID"
            value={turnIdFilter}
            onChange={(e) => setTurnIdFilter(e.target.value)}
            className="w-48 bg-white dark:bg-slate-800"
          />
          <TextField
            size="small"
            label="Action ID"
            value={actionIdFilter}
            onChange={(e) => setActionIdFilter(e.target.value)}
            className="w-48 bg-white dark:bg-slate-800"
          />
          <TextField
            size="small"
            label="Recovery ID"
            value={recoveryIncidentIdFilter}
            onChange={(e) => setRecoveryIncidentIdFilter(e.target.value)}
            className="w-48 bg-white dark:bg-slate-800"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshCcw size={16} />}
            onClick={() => refetch()}
            className="text-gray-700 border-gray-300 hover:bg-gray-100 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-800 h-10"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Table Card */}
      <Box className="rounded-xl bg-white shadow-sm dark:bg-slate-800">
        <Box className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-slate-700">
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Audit Trail
          </Typography>
        </Box>

        {isLoading ? (
          <Box className="flex items-center justify-center p-12">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box className="p-12">
            <Typography className="text-center text-red-600 dark:text-red-400">
              Failed to load audit logs. Please try again.
            </Typography>
          </Box>
        ) : logs.length > 0 ? (
          <DataGrid
            rows={logs}
            columns={columns}
            getRowId={(row) => row.publicId}
            loading={isFetching}
            disableRowSelectionOnClick
            paginationMode="server"
            rowCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={(model) => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            autoHeight
            className="border-0"
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider',
              },
            }}
          />
        ) : (
          <Box className="p-12">
            <EmptyState
              Icon={Shield}
              title="No Audit Logs"
              description="No audit events match the current filters."
            />
          </Box>
        )}
      </Box>

      {/* JSON Detail Dialog */}
      <Dialog
        open={selectedEventData !== null}
        onClose={() => setSelectedEventData(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="bg-slate-50 dark:bg-slate-800">
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
            Event Data payload
          </Typography>
        </DialogTitle>
        <DialogContent className="mt-4">
          <Box className="bg-gray-900 rounded-md p-4 overflow-auto max-h-[60vh]">
            <pre className="text-green-400 text-sm whitespace-pre-wrap">
              {JSON.stringify(selectedEventData, null, 2)}
            </pre>
          </Box>
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={() => setSelectedEventData(null)} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
